/**
 * Central SEO configuration. Used by the <SEO> component, the JsonLd helper,
 * and the build-time react-snap prerender to keep titles, descriptions, and
 * Schema.org metadata consistent across the site.
 */

export const SITE_URL = 'https://freshfitlab.io';
export const SITE_NAME = 'FreshFitLab';
export const SITE_TAGLINE = 'Recipes, Meal Plans, Workouts & Coaching';
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.jpg`;
export const TWITTER_HANDLE = '@freshmenulab';

export const APP_STORE_URL = 'https://apps.apple.com/us/app/freshmenulab/id6758483247';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.freshmenulab';

export const DEFAULT_TITLE = `${SITE_NAME} — ${SITE_TAGLINE}`;
export const DEFAULT_DESCRIPTION =
  'FreshFitLab is the all-in-one platform for meal planning, fitness training, and personal coaching. Browse 500+ recipes, follow workout programs with 2,000+ exercises, build smart meal plans, shop with one tap via Instacart, and connect with certified coaches.';

export const DEFAULT_KEYWORDS = [
  'meal planning',
  'recipes',
  'workout app',
  'fitness app',
  'meal planner',
  'workout planner',
  'shopping list',
  'instacart',
  'fitness coaching',
  'nutrition coaching',
  'exercise library',
];

/** Joins SITE_URL + a path, ensuring a single slash. */
export const absoluteUrl = (path = '/') => {
  if (!path) return SITE_URL;
  if (/^https?:\/\//.test(path)) return path;
  return `${SITE_URL}${path.startsWith('/') ? '' : '/'}${path}`;
};

/**
 * Per-route copy used by marketing pages. The keys are pathnames; each value
 * provides a title + description that shows up in browser tabs, Google SERPs,
 * and as fallbacks when AI engines need a one-line summary.
 */
export const PAGE_SEO = {
  '/': {
    title: `${SITE_NAME} — Meal Planning, Recipes, Workouts & Coaching`,
    description: DEFAULT_DESCRIPTION,
    keywords: DEFAULT_KEYWORDS,
  },
  '/meal-planning': {
    title: 'Meal Planning Made Easy — Recipes & Smart Shopping Lists | FreshFitLab',
    description:
      'Plan your meals, build a weekly menu, and shop in one tap with Instacart. Browse 500+ recipes filtered by diet, cuisine, prep time, and macros.',
    keywords: [
      'meal planning',
      'weekly meal plan',
      'meal planner app',
      'recipes',
      'instacart meal planning',
      'macro meal planning',
      'shopping list app',
    ],
  },
  '/fitness-training': {
    title: 'Fitness Training App — Workout Programs & 2,000+ Exercises | FreshFitLab',
    description:
      'Follow structured workout programs, browse 2,000+ exercises with form videos, schedule training, and track every session. Strength, cardio, mobility, and more.',
    keywords: [
      'workout app',
      'workout planner',
      'training program app',
      'exercise library',
      'strength training app',
      'fitness tracker',
    ],
  },
  '/coaching': {
    title: 'Online Fitness & Nutrition Coaches — Personalized Plans | FreshFitLab',
    description:
      'Connect with certified fitness and nutrition coaches who build personalized workout programs and meal plans for you. Book sessions, message your coach, and track progress in one place.',
    keywords: [
      'online fitness coach',
      'online nutrition coach',
      'personal trainer app',
      'nutrition coaching',
      'fitness coaching',
      'remote personal trainer',
    ],
  },
  '/for-business': {
    title: 'For Physical Therapy, Gyms & Nutrition Practices | FreshFitLab Business',
    description:
      'Manage your wellness practice in one place. Onboard clients via QR code, assign exercises and meal plans, and track outcomes — built for PT clinics, nutrition practices, and gyms.',
    keywords: [
      'physical therapy software',
      'nutrition practice software',
      'gym client management',
      'wellness business platform',
      'exercise prescription software',
    ],
  },
  '/pricing': {
    title: 'Pricing — Free to Premium for Members, Creators & Businesses | FreshFitLab',
    description:
      'Simple, transparent pricing for FreshFitLab. Free for everyone with optional premium tiers for power users, creators, coaches, and businesses.',
  },
  '/faq': {
    title: 'Frequently Asked Questions | FreshFitLab',
    description:
      'Answers to common questions about FreshFitLab — meal planning, recipes, workouts, coaching, business plans, billing, and account management.',
  },
  '/menu': {
    title: 'Browse 500+ Recipes — Filter by Diet, Cuisine & Prep Time | FreshFitLab',
    description:
      'Discover hundreds of healthy, easy-to-make recipes. Filter by dietary preference, cuisine, prep time, and macros. Save favorites and build a weekly meal plan.',
  },
  '/meal-plan': {
    title: 'Build Your Weekly Meal Plan | FreshFitLab',
    description:
      'Drag and drop recipes into a weekly meal plan. FreshFitLab auto-builds your shopping list and sends it to Instacart in one tap.',
  },
  '/nutrition/planner': {
    title: 'Nutrition Planner — Set Calorie & Macro Goals | FreshFitLab',
    description:
      'Set calorie and macro targets, build a nutrition plan, and align your meals with your fitness goals.',
  },
  '/fitness': {
    title: 'Workout Programs — Multi-Week Training Plans | FreshFitLab',
    description:
      'Browse multi-week workout programs designed by certified coaches. Strength, hypertrophy, conditioning, mobility, and rehab programs.',
  },
  '/fitness/workouts': {
    title: 'Workouts — Hundreds of Free Workout Routines | FreshFitLab',
    description:
      'Hundreds of free workouts for every goal — strength, cardio, mobility, and more. Filter by duration, equipment, and difficulty.',
  },
  '/fitness/exercises': {
    title: 'Exercise Library — 2,000+ Exercises with Videos & Instructions | FreshFitLab',
    description:
      'Detailed exercise guides with step-by-step instructions, target muscles, equipment needed, and instructional videos. Strength, cardio, mobility, and rehab.',
  },
  '/coaches': {
    title: 'Browse Online Coaches | FreshFitLab',
    description:
      'Browse certified fitness and nutrition coaches. View profiles, specializations, programs, and book sessions.',
  },
  '/creators': {
    title: 'Recipe Creators — Browse Top Food Creators | FreshFitLab',
    description:
      'Discover top recipe creators on FreshFitLab. Follow your favorites, browse their menus, and shop their recipes.',
  },
  '/affiliate/register': {
    title: 'Become a Creator or Coach | FreshFitLab',
    description:
      'Publish recipes or workout programs, train clients, and earn commission on Instacart orders and coaching sales. Join the FreshFitLab creator and coach program.',
  },
  '/community-kitchen': {
    title: 'Community Kitchen — Share a Meal, Make a Difference | FreshFitLab',
    description:
      'Community Kitchen connects volunteer cooks, donors, and people in need. Cook, fund, or receive a home-cooked meal in your neighborhood.',
  },
  '/privacy': {
    title: 'Privacy Policy | FreshFitLab',
    description: 'Read the FreshFitLab privacy policy.',
    robots: 'noindex,follow',
  },
  '/terms': {
    title: 'Terms of Service | FreshFitLab',
    description: 'Read the FreshFitLab terms of service.',
    robots: 'noindex,follow',
  },
  '/delete-account': {
    title: 'Account Deletion | FreshFitLab',
    description: 'Permanently delete your FreshFitLab account and personal data.',
    robots: 'noindex,follow',
  },
};

/** Brand-level JSON-LD that should appear on the home page. */
export const ORGANIZATION_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: SITE_NAME,
  url: SITE_URL,
  logo: `${SITE_URL}/logo192.png`,
  email: 'support@freshfitlab.io',
  sameAs: [APP_STORE_URL, PLAY_STORE_URL],
  description: DEFAULT_DESCRIPTION,
};

export const WEBSITE_JSONLD = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  potentialAction: {
    '@type': 'SearchAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: `${SITE_URL}/menu?search={search_term_string}`,
    },
    'query-input': 'required name=search_term_string',
  },
};

export const MOBILE_APP_JSONLD = [
  {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: `${SITE_NAME} (iOS)`,
    operatingSystem: 'iOS',
    applicationCategory: 'HealthApplication',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    downloadUrl: APP_STORE_URL,
  },
  {
    '@context': 'https://schema.org',
    '@type': 'MobileApplication',
    name: `${SITE_NAME} (Android)`,
    operatingSystem: 'ANDROID',
    applicationCategory: 'HealthApplication',
    offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD' },
    downloadUrl: PLAY_STORE_URL,
  },
];

/** Build a BreadcrumbList JSON-LD object from an array of {name, url}. */
export const buildBreadcrumbs = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: item.name,
    item: absoluteUrl(item.url),
  })),
});

/** Get per-route SEO config, falling back to defaults. */
export const getPageSeo = (pathname) =>
  PAGE_SEO[pathname] || {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
  };
